import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Calendar, Package, Search, Bell, Clock, CheckCircle, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  handleSearchInputChange,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';
import margDataService from '../../../api/crm/marg/margDataService';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import { toast } from 'react-hot-toast';
import { Download } from 'lucide-react';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';

const BatchExpiryManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial filter values from URL (for persistence across refresh)
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || 10;
  const initialSortBy = searchParams.get('sortBy') || 'expiryDate';
  const initialSortDir = searchParams.get('sortOrder') || 'asc';
  const isInitialFilterMount = useRef(true);

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [filterStatus, setFilterStatus] = useState(initialStatus);
  const searchInputRef = useRef(null);
  const [batchData, setBatchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [pagination, setPagination] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalBatches: 0,
    activeBatches: 0,
    expiringSoon: 0,
    expiredBatches: 0,
    criticalAlerts: 0
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortDir);

  // New states for export and selection
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset to page 1 when filters change (skip on initial mount so the
  // URL-restored page is preserved on refresh)
  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setCurrentPage(1);
    setSelectedRows(new Set());
  }, [debouncedSearchTerm, filterStatus, dateRange, itemsPerPage]);

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'status', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn && sortColumn !== 'expiryDate') params.set('sortBy', sortColumn);
      if (sortDirection && sortDirection !== 'asc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filterStatus,
    dateRange,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortDirection,
    setSearchParams,
  ]);

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Calculate expiry status
  const calculateBatchStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'active', alertLevel: 'none' };

    const expiry = new Date(expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry - today;
    const daysToExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let status = 'active';
    let alertLevel = 'none';

    if (daysToExpiry < 0) {
      status = 'expired';
      alertLevel = 'expired';
    } else if (daysToExpiry <= 30) {
      status = 'expiring_soon';
      alertLevel = 'critical';
    } else if (daysToExpiry <= 90) {
      status = 'expiring_soon';
      alertLevel = 'warning';
    }

    return { status, alertLevel };
  };

  // Reusable function to fetch batch data from backend
  const fetchBatchData = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const companyId = process.env.REACT_APP_MARG_COMPANY_ID || null;
      // Prepare search parameter - only send if there's a non-empty search term
      const searchParam = debouncedSearchTerm && debouncedSearchTerm.trim() !== '' ? debouncedSearchTerm.trim() : undefined;

      // Build request parameters - all filtering happens on backend
      const requestParams = {
        companyId,
        page: currentPage,
        limit: itemsPerPage,
        hasBatchAndExpiry: true, // Only get items with both batch and expiry
        expiryStatus: filterStatus !== 'all' ? filterStatus : undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined
      };

      // Only add search if it has a value
      if (searchParam) {
        requestParams.search = searchParam;
      }

      // Fetch inventory data - backend handles all filtering (batch/expiry and expiry status)
      const result = await margDataService.getInventory(requestParams);

      if (result.success) {
        const inventory = result.inventory || [];

        // Transform batches - calculate status for display
        const transformedBatches = inventory.map((item) => {
          const { status, alertLevel } = calculateBatchStatus(item.expiry);

          // Use actual data from Marg - no fallbacks
          return {
            ...item, // Include all fields from backend
            id: item.id || `BATCH-${item.productId}-${item.batch}`,
            batchNumber: item.batch,
            status,
            alertLevel,
            // Format dates using formatDate utility
            manufacturingDate: item.batchDate ? formatDate(item.batchDate, { includeTime: false }) : undefined,
            expiryDate: item.expiry ? formatDate(item.expiry, { includeTime: false }) : undefined,
            supplierDate: item.supplierDate ? formatDate(item.supplierDate, { includeTime: false }) : undefined,
            lastUpdated: item.syncedAt ? formatDate(item.syncedAt, { includeTime: true }) : undefined
          };
        });

        setBatchData(transformedBatches);

        // Use backend pagination
        if (result.pagination) {
          const backendPagination = result.pagination;
          const normalizedPagination = {
            currentPage: backendPagination.currentPage || backendPagination.page || 1,
            totalPages: backendPagination.totalPages || backendPagination.pages || 1,
            totalBatches: backendPagination.totalInventory || backendPagination.total || 0,
            hasNextPage: backendPagination.hasNextPage !== undefined
              ? backendPagination.hasNextPage
              : (backendPagination.currentPage || backendPagination.page || 1) < (backendPagination.totalPages || backendPagination.pages || 1),
            hasPrevPage: backendPagination.hasPrevPage !== undefined
              ? backendPagination.hasPrevPage
              : (backendPagination.currentPage || backendPagination.page || 1) > 1,
            limit: backendPagination.limit || itemsPerPage || 10
          };
          setPagination(normalizedPagination);
        } else {
          setPagination(null);
        }
      } else {
        setBatchData([]);
        setPagination(null);
      }
    } catch (error) {
      setBatchData([]);
      setPagination(null);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filterStatus, dateRange]);

  // Fetch batch data from API
  useEffect(() => {
    fetchBatchData();
  }, [fetchBatchData]);

  // Reusable function to fetch analytics independently (using optimized backend endpoint)
  const fetchAnalyticsData = useCallback(async () => {
    try {
      const companyId = process.env.REACT_APP_MARG_COMPANY_ID || null;
      const result = await margDataService.getInventoryAnalytics({
        companyId
      });

      if (result.success) {
        setAnalytics({
          totalBatches: result.totalBatches || 0,
          activeBatches: result.activeBatches || 0,
          expiringSoon: result.expiringSoon || 0,
          expiredBatches: result.expiredBatches || 0,
          criticalAlerts: 0 // Unused in UI, but kept for state shape consistency
        });
      }
    } catch (error) {
    }
  }, []);

  // Fetch analytics independently on mount
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'expiring_soon': return 'text-yellow-600 bg-yellow-100';
      case 'expired': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return debouncedSearchTerm.trim() !== '' || filterStatus !== 'all' || dateRange.startDate !== '' || dateRange.endDate !== '';
  }, [debouncedSearchTerm, filterStatus, dateRange]);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterStatus('all');
    setDateRange({ startDate: '', endDate: '' });
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted batch data
  const getSortedBatchData = (data) => {
    if (!sortColumn || !data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'productName') {
        aValue = (a.productName || '').toLowerCase();
        bValue = (b.productName || '').toLowerCase();
      } else if (sortColumn === 'dates') {
        aValue = new Date(a.expiry || 0).getTime();
        bValue = new Date(b.expiry || 0).getTime();
      } else if (sortColumn === 'stock') {
        aValue = a.stock || 0;
        bValue = b.stock || 0;
      } else if (sortColumn === 'productCode') {
        aValue = (a.productCode || '').toLowerCase();
        bValue = (b.productCode || '').toLowerCase();
      } else if (sortColumn === 'cost') {
        aValue = a.cost || 0;
        bValue = b.cost || 0;
      } else if (sortColumn === 'mrp') {
        aValue = a.mrp || 0;
        bValue = b.mrp || 0;
      } else if (sortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortColumn === 'expiry') {
        aValue = new Date(a.expiry || 0).getTime();
        bValue = new Date(b.expiry || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      const order = sortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const mapBatchesForExport = (batchesToExport) => {
    return batchesToExport.map(batch => ({
      'Product Name': batch.productName || 'N/A',
      'Batch': batch.batchNumber || 'N/A',
      'Stock': `${batch.stock ?? 0}${batch.unit ? ' ' + batch.unit : ''}`,
      'Expiry Date': batch.expiryDate || 'N/A',
      'Manufacturing Date': batch.manufacturingDate || 'N/A',
      'MRP': batch.mrp || 0,
      'Cost': batch.cost || 0,
      'Status': (batch.status || '').replace('_', ' ').toUpperCase(),
      'Last Updated': batch.lastUpdated || 'N/A'
    }));
  };

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }

    const raf = requestAnimationFrame(() => {
      setInlineFormatPanelEntered(true);
    });

    const handleClickOutside = (event) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(event.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInlineSelectedFormats]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'batch-expiry',
            format,
            scope,
            filters: overrideFilters ?? {
              search: debouncedSearchTerm.trim() || undefined,
              expiryStatus: filterStatus !== 'all' ? filterStatus : undefined,
              startDate: dateRange.startDate || undefined,
              endDate: dateRange.endDate || undefined,
              companyId: process.env.REACT_APP_MARG_COMPANY_ID || null,
            },
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortColumn,
            sortOrder: sortDirection,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1])
        : generateExportFilename('batch-expiry', format);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      debouncedSearchTerm,
      filterStatus,
      dateRange,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortDirection,
    ]
  );

  const handleBatchSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;

      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: Array.from(selectedRows),
        });
        setSelectedRows(new Set());
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleBatchScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;

      setExportLoading(true);
      try {
        const params = { scope, format };

        if (scope === 'dateRange' && meta.startDate && meta.endDate) {
          params.overrideFilters = {
            startDate: meta.startDate,
            endDate: meta.endDate,
            companyId: process.env.REACT_APP_MARG_COMPANY_ID || null,
          };
        }

        if (scope === 'all') {
          params.overrideFilters = {
            companyId: process.env.REACT_APP_MARG_COMPANY_ID || null,
          };
        }

        await requestBackendExport(params);
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [requestBackendExport]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((prev) => !prev);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = batchData.map(batch => batch.id || batch._id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Batch & Expiry Management</h1>
            <p className="text-gray-600">Monitor product batches, expiry dates, and stock levels</p>
          </div>
          <PermissionGate permission="order_management" action="read">
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={!batchData || batchData.length === 0}
              >
                {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
              </Button>

              <SelectedExportFormatBar
                isOpen={showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                onExport={handleBatchSelectedFormatExport}
                loading={exportLoading}
              />
            </div>
          </PermissionGate>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Batches</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totalBatches}</p>
            </div>
            <Package className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Batches</p>
              <p className="text-2xl font-bold text-green-600">{analytics.activeBatches}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-bold text-yellow-600">{analytics.expiringSoon}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Expired</p>
              <p className="text-2xl font-bold text-red-600">{analytics.expiredBatches}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search products,BatchNo"
              value={searchTerm}
              onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
              maxLength={FIELD_LIMITS.search}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
              autoComplete="off"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expiring_soon">Expiring Soon</option>
              <option value="expired">Expired</option>
            </select>
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setCurrentPage(1);
              }}
              allowFutureDates={true}
            />
            {hasActiveFilters && (
              <PermissionGate permission="order_management" action="read">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="flex-shrink-0 whitespace-nowrap"
                >
                  Clear
                </Button>
              </PermissionGate>
            )}
          </div>
        </div>
      </div>

      {/* Batch Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Batch Items</h3>
        </div>

        {/* Loading State - Only show in table area, not full page */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading batch data...</p>
            </div>
          </div>
        )}

        {/* Batch Table View */}
        {!loading && (
          <div className="overflow-x-auto">
            {batchData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Package className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 font-medium">No batch data found</p>
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          checked={batchData.length > 0 && selectedRows.size === batchData.length}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('productName')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Product & Batch</span>
                          {sortColumn === 'productName' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('dates')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Dates</span>
                          {sortColumn === 'dates' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('stock')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Quantity</span>
                          {sortColumn === 'stock' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('productCode')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Product Code</span>
                          {sortColumn === 'productCode' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('cost')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Cost</span>
                          {sortColumn === 'cost' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('mrp')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>MRP</span>
                          {sortColumn === 'mrp' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {sortColumn === 'status' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('expiry')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Expiry</span>
                          {sortColumn === 'expiry' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getSortedBatchData(batchData).map((batch) => {
                      const batchId = batch.id || batch._id;
                      const isSelected = selectedRows.has(batchId);
                      return (
                        <tr key={batchId} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              checked={isSelected}
                              onChange={() => handleSelectRow(batchId)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {batch.productName ? (
                              <div className="text-sm font-medium text-gray-900">{batch.productName}</div>
                            ) : null}
                            {batch.batchNumber && (
                              <div className="text-sm text-gray-500">Batch: {batch.batchNumber}</div>
                            )}
                            {batch.productId && (
                              <div className="text-xs text-gray-500">ID: {batch.productId}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {batch.manufacturingDate && (
                              <div className="text-sm text-gray-900 flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Mfg: {batch.manufacturingDate}
                              </div>
                            )}
                            {batch.expiryDate && (
                              <div className="text-sm text-gray-500 flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Exp: {batch.expiryDate}
                              </div>
                            )}
                            {!batch.manufacturingDate && !batch.expiryDate && (
                              <div className="text-sm text-gray-400">N/A</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {batch.stock !== undefined && batch.stock !== null ? (
                              <div className="text-sm text-gray-900">{batch.stock}</div>
                            ) : null}
                            {batch.opening !== undefined && batch.opening !== null && batch.opening > 0 && (
                              <div className="text-xs text-gray-500">Opening: {batch.opening}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {batch.productCode ? (
                              <div className="text-sm text-gray-900">{batch.productCode}</div>
                            ) : null}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {batch.cost !== undefined && batch.cost !== null ? `₹${batch.cost.toLocaleString()}` : <span className="text-gray-400">N/A</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {batch.mrp !== undefined && batch.mrp !== null ? `₹${batch.mrp.toLocaleString()}` : <span className="text-gray-400">N/A</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {batch.status && (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(batch.status)}`}>
                                {batch.status.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {batch.expiry ? (
                              <div className={`text-sm font-medium ${batch.status === 'expired' ? 'text-red-600' : 'text-gray-900'}`}>
                                {formatDate(batch.expiry, { includeTime: false })}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">N/A</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                {(() => {
                  const totalBatches = Number(pagination?.totalBatches || batchData.length || 0);
                  const totalPages = Number(pagination?.totalPages || 1);
                  const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
                  // Show pagination if there's more than one page
                  const shouldShowPagination = !loading && totalPages > 1;

                  return shouldShowPagination ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                      <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                        {/* Results Count and Limit Selector */}
                        <div className="flex items-center space-x-4">
                          <div className="text-sm text-gray-700">
                            Showing {((pagination?.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((pagination?.currentPage || 1) * effectivePaginationLimit, totalBatches)} of {totalBatches} results
                          </div>
                          <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-700">Items per page:</label>
                            <select
                              value={itemsPerPage}
                              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                              className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                            >
                              <option value={3}>3</option>
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                        </div>

                        {/* Pagination Controls */}
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handlePageChange((pagination?.currentPage || 1) - 1)}
                            disabled={pagination?.hasPrevPage === false || (pagination?.currentPage || 1) <= 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Previous
                          </button>

                          {/* Page Numbers */}
                          <div className="flex items-center space-x-1">
                            {(() => {
                              const currentPageNum = pagination?.currentPage || 1;
                              const pages = [];

                              if (totalPages <= 10) {
                                for (let i = 1; i <= totalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push(1);

                                if (currentPageNum <= 4) {
                                  for (let i = 2; i <= 5; i++) {
                                    pages.push(i);
                                  }
                                  pages.push('ellipsis');
                                  pages.push(totalPages);
                                } else if (currentPageNum >= totalPages - 3) {
                                  pages.push('ellipsis');
                                  for (let i = totalPages - 4; i <= totalPages; i++) {
                                    pages.push(i);
                                  }
                                } else {
                                  pages.push('ellipsis');
                                  for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                    pages.push(i);
                                  }
                                  pages.push('ellipsis');
                                  pages.push(totalPages);
                                }
                              }

                              return pages.map((page, index) => {
                                if (page === 'ellipsis') {
                                  return (
                                    <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                      ...
                                    </span>
                                  );
                                }

                                return (
                                  <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                      ? 'bg-agrosaf-green text-white'
                                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                      }`}
                                  >
                                    {page}
                                  </button>
                                );
                              });
                            })()}
                          </div>

                          <button
                            onClick={() => handlePageChange((pagination?.currentPage || 1) + 1)}
                            disabled={pagination?.hasNextPage === false || (pagination?.currentPage || 1) >= (pagination?.totalPages || 1)}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
              </>
            )}
          </div>
        )}
      </div>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Batches"
        showDateRange
        scopedFormatExport={{
          onExport: handleBatchScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div>
  );
};

export default BatchExpiryManagement;
